import { useCallback, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Box,
} from '@shopify/polaris';
import { useWooBillingStatus, useWooBillingCheckout, useWooBillingPortal } from '../hooks/api';

// WooCommerce billing (Stripe). Self-service: pick a plan -> Stripe Checkout;
// manage/cancel -> Stripe Customer Portal. The plan is enforced server-side via
// the Stripe webhook -> bridge Shop.plan, so this UI only kicks off the flows.
const WOO_PLANS: Array<{ key: string; name: string; blurb: string }> = [
  { key: 'STARTER', name: 'Starter', blurb: 'Google Ads, Meta CAPI, GA4' },
  { key: 'GROWTH', name: 'Growth', blurb: '+ TikTok, Pinterest, Snapchat, advanced analytics' },
  { key: 'PRO', name: 'Pro', blurb: '+ alerts, CSV export, outbound webhooks, API access' },
];

export function BillingWoo() {
  const { data: status, isLoading } = useWooBillingStatus();
  const checkout = useWooBillingCheckout();
  const portal = useWooBillingPortal();
  const [error, setError] = useState<string | null>(null);

  const currentPlan = status?.plan || 'STARTER';

  const choose = useCallback(
    async (plan: string) => {
      setError(null);
      try {
        const { url } = await checkout.mutateAsync(plan);
        window.location.href = url;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start checkout.');
      }
    },
    [checkout],
  );

  const manage = useCallback(async () => {
    setError(null);
    try {
      const { url } = await portal.mutateAsync();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the billing portal.');
    }
  }, [portal]);

  return (
    <Page title="Billing">
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}

            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Current plan
                  </Text>
                  <Badge tone="success">{isLoading ? '…' : currentPlan}</Badge>
                </InlineStack>
                {status?.hasSubscription && (
                  <Box>
                    <Button onClick={manage} loading={portal.isPending}>
                      Manage billing
                    </Button>
                  </Box>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Choose a plan
                </Text>
                {WOO_PLANS.map((p) => (
                  <InlineStack key={p.key} align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {p.name}
                      </Text>
                      <Text as="span" tone="subdued" variant="bodySm">
                        {p.blurb}
                      </Text>
                    </BlockStack>
                    <Button
                      variant={p.key === currentPlan ? 'secondary' : 'primary'}
                      disabled={p.key === currentPlan}
                      loading={checkout.isPending}
                      onClick={() => choose(p.key)}
                    >
                      {p.key === currentPlan ? 'Current' : 'Choose'}
                    </Button>
                  </InlineStack>
                ))}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
